(() => {
  const focusable = 'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';
  const returnFocus = new WeakMap();

  const announce = (message) => {
    const region = document.getElementById('StorefrontStatus');
    if (!region) return;
    region.textContent = '';
    window.requestAnimationFrame(() => { region.textContent = message; });
  };

  const bindDialog = (dialog) => {
    if (!dialog || dialog.dataset.dialogBound === 'true') return;
    dialog.dataset.dialogBound = 'true';
    dialog.addEventListener('close', () => {
      document.querySelectorAll(`[aria-controls="${dialog.id}"]`).forEach((control) => control.setAttribute('aria-expanded', 'false'));
      returnFocus.get(dialog)?.focus();
      returnFocus.delete(dialog);
    });
    dialog.addEventListener('click', (event) => {
      if (event.target === dialog) closeDialog(dialog);
    });
  };

  const openDialog = (dialog, trigger) => {
    if (!dialog) return;
    bindDialog(dialog);
    returnFocus.set(dialog, trigger || document.activeElement);
    dialog.showModal();
    trigger?.setAttribute('aria-expanded', 'true');
    const first = dialog.querySelector('[autofocus]') || dialog.querySelector(focusable);
    first?.focus();
  };

  const closeDialog = (dialog) => {
    if (dialog?.open) dialog.close();
  };

  const readVariantState = (form) => {
    const script = form.parentElement?.querySelector('[data-product-variant-state]');
    if (!script) return [];
    try { return JSON.parse(script.textContent); } catch { return []; }
  };

  const syncSellingPlanMode = (form) => {
    const picker = form?.querySelector('[data-selling-plan-picker]');
    const cadence = form?.querySelector('[data-selling-plan-cadence]');
    const select = form?.querySelector('[data-selling-plan-select]');
    if (!picker || !cadence || !select) return;
    const availableOptions = [...select.options].filter((option) => !option.hidden && !option.disabled);
    const subscriptionRow = picker.querySelector('[data-subscription-option]');
    const subscriptionMode = picker.querySelector('[data-purchase-mode][value="subscription"]');
    const hasPlans = availableOptions.length > 0;
    if (subscriptionRow) subscriptionRow.hidden = !hasPlans;
    if (subscriptionMode) subscriptionMode.disabled = !hasPlans;
    const subscribed = hasPlans && picker.querySelector('[data-purchase-mode]:checked')?.value === 'subscription';
    cadence.hidden = !subscribed;
    cadence.classList.toggle('is-visible', subscribed);
    select.disabled = !subscribed;
    if (subscribed && (!select.selectedOptions[0] || select.selectedOptions[0].disabled || select.selectedOptions[0].hidden)) {
      select.value = availableOptions[0]?.value || '';
    }
    const selected = subscribed ? select.selectedOptions[0] : availableOptions[0];
    const subscriptionPrice = picker.querySelector('[data-subscription-price]');
    if (subscriptionPrice) subscriptionPrice.textContent = selected?.dataset.planPrice || '';
  };

  const updateProductForm = (form, variantId) => {
    const state = readVariantState(form);
    const variant = state.find((candidate) => String(candidate.id) === String(variantId));
    if (!variant) return;
    const section = form.closest('[data-product-section]');
    const price = section?.querySelector('.product-price .price strong');
    if (price) price.textContent = variant.price;
    const button = form.querySelector('[data-add-to-cart]');
    if (button) {
      const canPurchase = variant.available && (form.dataset.requiresSellingPlan !== 'true' || variant.plans.length > 0);
      button.disabled = !canPurchase;
      button.textContent = canPurchase ? button.dataset.availableLabel : button.dataset.unavailableLabel;
    }
    const plans = new Map(variant.plans.map((plan) => [String(plan.id), plan]));
    form.querySelectorAll('[data-selling-plan-option]').forEach((option) => {
      const plan = plans.get(String(option.dataset.sellingPlanOption));
      option.hidden = !plan;
      option.disabled = !plan;
      option.dataset.planPrice = plan?.price || '';
      option.textContent = plan ? `${option.dataset.planName} · ${plan.price}` : option.dataset.planName;
    });
    form.querySelectorAll('[data-one-time-price]').forEach((node) => { node.textContent = variant.price; });
    syncSellingPlanMode(form);
  };

  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-dialog-open]');
    if (opener) {
      event.preventDefault();
      openDialog(document.getElementById(opener.dataset.dialogOpen), opener);
      return;
    }
    const closer = event.target.closest('[data-dialog-close]');
    if (closer) closeDialog(closer.closest('dialog'));
  });

  document.addEventListener('change', (event) => {
    if (event.target.matches('[data-variant-radio]')) updateProductForm(event.target.form, event.target.value);
    if (event.target.matches('[data-purchase-mode], [data-selling-plan-select]')) syncSellingPlanMode(event.target.form);
  });

  // Native modal dialogs own Escape so only the topmost dialog closes.

  const refreshEvent = 'cart:refresh';
  document.addEventListener(refreshEvent, () => announce('Bag updated.'));

  document.addEventListener('DOMContentLoaded', () => {
    const header = document.querySelector('[data-site-header]');
    let headerFrame = null;
    const syncHeader = () => {
      header?.classList.toggle('is-compact', window.scrollY > 72);
      headerFrame = null;
    };
    window.addEventListener('scroll', () => {
      if (!headerFrame) headerFrame = window.requestAnimationFrame(syncHeader);
    }, { passive: true });
    syncHeader();
    document.querySelectorAll('dialog').forEach(bindDialog);
    document.querySelectorAll('[data-product-form]').forEach((form) => {
      const selected = form.querySelector('[data-variant-radio]:checked');
      if (selected) updateProductForm(form, selected.value);
      syncSellingPlanMode(form);
    });
    document.querySelector('[data-form-status-focus]')?.focus();

    const storefrontActions = window.Shopify?.actions;
    if (storefrontActions?.updateCart?.configure) {
      storefrontActions.updateCart.configure({
        eventTarget: () => document,
        async handler(defaultHandler) {
          const result = await defaultHandler();
          try {
            await window.SoBotanicalCart?.refreshDrawer();
          } catch {
            announce('Open the cart page to review your bag; the drawer could not refresh.');
          }
          return result;
        }
      });
    }
    if (storefrontActions?.openCart?.configure) {
      storefrontActions.openCart.configure({
        handler() {
          const drawer = document.getElementById('CartDrawer');
          if (drawer) openDialog(drawer, document.activeElement);
          else window.location.assign(`${window.Shopify?.routes?.root || '/'}cart`);
        }
      });
    }
    if (storefrontActions) document.documentElement.dataset.shopifyActions = 'available';
  });

  window.SoBotanicalTheme = { announce, openDialog, closeDialog, bindDialog, returnFocus, refreshEvent };
})();

// Disclosure navigation keeps category links navigable and child menus accessible.
(() => {
  const close = (button, restoreFocus = false) => {
    button.setAttribute('aria-expanded', 'false');
    const panel = document.getElementById(button.getAttribute('aria-controls'));
    if (panel) panel.hidden = true;
    if (restoreFocus) button.focus();
  };
  document.addEventListener('click', event => {
    const button = event.target.closest('.nav-toggle');
    if (button) {
      const open = button.getAttribute('aria-expanded') !== 'true';
      const item = button.closest('.nav-item');
      item.parentElement.querySelectorAll(':scope > .nav-item > .nav-item-row > .nav-toggle').forEach(other => { if (other !== button) close(other); });
      button.setAttribute('aria-expanded', String(open));
      const panel = document.getElementById(button.getAttribute('aria-controls'));
      if (panel) panel.hidden = !open;
    } else if (!event.target.closest('.primary-nav, .mobile-nav')) {
      document.querySelectorAll('.nav-toggle[aria-expanded="true"]').forEach(b => close(b));
    }
  });
  document.addEventListener('keydown', event => {
    if (event.key !== 'Escape') return;
    const item = event.target.closest('.nav-item');
    const button = item?.querySelector(':scope > .nav-item-row > .nav-toggle[aria-expanded="true"]') || event.target.closest('.nav-submenu')?.previousElementSibling?.querySelector('.nav-toggle');
    if (button) { event.preventDefault(); close(button, true); }
  });
  document.addEventListener('focusin', event => {
    document.querySelectorAll('.nav-toggle[aria-expanded="true"]').forEach(button => {
      if (!button.closest('.nav-item').contains(event.target)) close(button);
    });
  });
})();

// Category choices reveal a short, curated selection; the full collection stays below.
document.addEventListener('click', event => {
  const button = event.target.closest('[data-discovery-choice]');
  if (!button) return;
  const section = button.closest('.category-discovery');
  section.querySelectorAll('[data-discovery-choice]').forEach(choice => choice.setAttribute('aria-expanded', String(choice === button)));
  section.querySelectorAll('.discovery-panel').forEach(panel => { panel.hidden = panel.id !== button.getAttribute('aria-controls'); });
});

// Small First Aid collections: filter the single rendered product grid.
document.addEventListener('click', (event) => {
  const button = event.target.closest('[data-native-firstaid] [data-firstaid-handles]');
  if (!button) return;
  const control = button.closest('[data-native-firstaid]');
  const section = control.closest('.collection-shop');
  const handles = button.dataset.firstaidHandles.split(',').filter(Boolean);
  control.querySelectorAll('button').forEach(item => item.setAttribute('aria-pressed', String(item === button)));
  let count = 0;
  section.querySelectorAll('.product-grid > [data-product-handle]').forEach(card => {
    card.hidden = handles.length > 0 && !handles.includes(card.dataset.productHandle);
    if (!card.hidden) count++;
  });
  const result = section.querySelector('.result-count');
  if (result) result.textContent = `${count} products`;
});
